// packages/app/src/scaffolder/AzureDevOpsProjectPicker/AzureDevOpsProjectPickerExtension.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { TextField, CircularProgress, Chip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, discoveryApiRef, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface Project {
  projectName: string;
}

interface ProjectsResponse {
  projects: Project[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  userEmail: string;
}

/**
 * Componente para seleccionar proyectos de Azure DevOps
 */
export const AzureDevOpsProjectPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [open, setOpen] = useState(false);

  // Obtener configuración del uiSchema
  const limit = uiSchema?.['ui:options']?.limit || 10;

  /**
   * Función para cargar proyectos desde el backend
   */
  const fetchProjects = useCallback(
    async (pageNum: number, searchQuery: string = '') => {
      setLoading(true);
      setError(null);

      try {
        // Obtener la URL base del backend usando DiscoveryApi
        const baseUrl = await discoveryApi.getBaseUrl('azuredevops-picker');
        
        // Obtener el token de autenticación del usuario
        const { token } = await identityApi.getCredentials();
        
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: limit.toString(),
        });

        if (searchQuery) {
          params.append('search', searchQuery);
        }

        const url = `${baseUrl}/projects?${params.toString()}`;
        
        // Usar FetchApi con token de autenticación manual
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        // Agregar token de autorización si está disponible
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetchApi.fetch(url, {
          method: 'GET',
          headers,
        });
        
        if (!response.ok) {
          throw new Error(`Error al cargar proyectos: ${response.status} ${response.statusText}`);
        }

        const data: ProjectsResponse = await response.json();
        
        // Si es la primera página, reemplaza; si no, agrega a la lista existente
        if (pageNum === 1) {
          setProjects(data.projects);
        } else {
          setProjects(prev => [...prev, ...data.projects]);
        }

        setHasMore(data.pagination.hasNext);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [discoveryApi, fetchApi, identityApi, limit]
  );

  // Cargar proyectos iniciales
  useEffect(() => {
    fetchProjects(1);
  }, []); // Sin dependencias para evitar re-renders innecesarios

  // Limpiar inputValue cuando se cierra el dropdown sin selección
  useEffect(() => {
    if (!open && !formData) {
      setInputValue('');
    }
  }, [open, formData]);

  /**
   * Maneja el cambio en el campo de búsqueda
   */
  const handleInputChange = (
    event: React.ChangeEvent<{}>, 
    value: string, 
    reason: string
  ) => {
    // Siempre actualizar el inputValue para mostrar lo que el usuario está escribiendo
    setInputValue(value);
    
    // Solo realizar búsqueda si es un cambio por input del usuario (no por selección)
    if (reason === 'input') {
      setPage(1);
      
      // Realizar búsqueda
      if (value.length > 0) {
        fetchProjects(1, value);
      } else {
        fetchProjects(1);
      }
    }
    // No hacer nada especial para 'reset' - dejar que Autocomplete maneje
  };

  /**
   * Maneja el scroll para cargar más proyectos (paginación infinita)
   */
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    
    if (
      listboxNode.scrollHeight - position <= 1 &&
      hasMore &&
      !loading
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchProjects(nextPage, inputValue);
    }
  };

  // Convertir projects a opciones (array de strings)
  const options = projects.map(p => p.projectName);

  return (
    <Autocomplete
        className={classes.root}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        freeSolo={false}
        clearOnBlur={false}
        selectOnFocus={true}
        value={formData || null}
        onChange={(event, newValue) => {
          onChange(newValue || undefined);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        loading={loading}
        loadingText="Cargando proyectos..."
        noOptionsText={
          error ? `Error: ${error}` : 'No se encontraron proyectos'
        }
        getOptionLabel={(option) => option}
        renderInput={params => (
          <TextField
            {...params}
            label="Proyecto de Azure DevOps"
            required={required}
            error={rawErrors && rawErrors.length > 0}
            helperText={
              rawErrors && rawErrors.length > 0
                ? rawErrors.join(', ')
                : `Busca y selecciona un proyecto`
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(option) => (
          <div>{option}</div>
        )}
        ListboxProps={{
          onScroll: handleScroll,
          style: { maxHeight: '250px' },
        }}
      />
  );
};

// La validación se exporta desde validation.ts